import cn from "classnames"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import Scrollable from "@/common/components/scrollable"
import Slider from "@/common/components/slider"
import env from "@/common/env"
import { ReactComponent as ColorBorderIcon } from "@/images/signomatic/color_border.svg"
import { ReactComponent as ColorFillIcon } from "@/images/signomatic/color_fill.svg"
import Icons from "@/images/signomatic/shapes"
import type Sign from "@/signomatic/app/sign"
import AutoGrid from "@/signomatic/components/auto_grid"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"
import FrameSizeLargeIcon from "@/skylticons/frame_size_large"
import FrameSizeSmallIcon from "@/skylticons/frame_size_small"
import FrameThicknessLargeIcon from "@/skylticons/frame_thickness_large"
import FrameThicknessSmallIcon from "@/skylticons/frame_thickness_small"

interface ShapeProps {
  name: string
  active: boolean
  disabled?: boolean
  update: () => void
}

const Shape = ({ name, active, update, disabled }: ShapeProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn("item", "selection", name, { active, disabled })} onClick={update}>
      <div className="image">{React.createElement(Icons[name])}</div>
      <div className="name paragraph-2xs center">
        {t(`shape_type.${name === "triangle" ? "triangle_border" : name}`)}
      </div>
    </div>
  )
}

interface ObjectBorderPickerOwnProps {
  sign: Sign
}

interface ObjectBorderPickerStateProps {
  data: SignData
  signState: SignState
  signType: string
  activeObject: SignObjectState
  subToolbox: string
}

interface ObjectBorderPickerDispatchProps {
  addSubToolbox: typeof actions.addSubToolbox
  setToolbox: typeof actions.setToolbox
}

type ObjectBorderPickerProps = ObjectBorderPickerStateProps &
  ObjectBorderPickerDispatchProps &
  ObjectBorderPickerOwnProps

const ObjectBorderDetails: React.FC<ObjectBorderPickerProps> = ({
  sign,
  data,
  signState,
  signType,
  subToolbox,
  activeObject,
  addSubToolbox,
  setToolbox,
}) => {
  const { t } = useTranslation()
  const [disabledReasonShown, showDisabledReason] = useState(false)
  const shapes = ["none", "rect", "round", "ellipse", "triangle"]

  if (!activeObject) {
    return null
  }

  const hasBorder = !!activeObject.border
  const disabled = signState.shape === "contour" || signType === "contour_cut"

  return (
    <div className="object-border">
      <div className="inline-toolbox border-shape">
        <AutoGrid>
          {shapes.map(shape => (
            <Shape
              key={shape}
              name={shape}
              active={shape === "none" ? !hasBorder : hasBorder && activeObject.border?.shape === shape}
              update={() => {
                if (disabled) {
                  return showDisabledReason(shape === "none" ? false : true)
                }

                if (shape === "none") {
                  if (["customcolor", "color"].includes(subToolbox)) {
                    setToolbox(null, subToolbox === "color" ? 2 : 3)
                  }
                }

                sign.updateObjectBorder(shape === "none" ? null : { shape })
              }}
              disabled={shape !== "none" && disabled}
            />
          ))}
        </AutoGrid>
      </div>
      {data.multicolor && (
        <div className="inline-toolbox border-color">
          <label>
            <strong>{t("toolbox.color")}:</strong>
          </label>
          <AutoGrid>
            <div
              className={cn("item", "selection", "color", { disabled: !hasBorder })}
              onClick={() => hasBorder && addSubToolbox("color", "object-border")}
            >
              <ColorBorderIcon />
              <div className="name paragraph-2xs center">{t("toolbox.border_color")}</div>
            </div>
            <div
              className={cn("item", "selection", "fill-color", { disabled: !hasBorder })}
              onClick={() => hasBorder && addSubToolbox("color", "object-fill")}
            >
              <ColorFillIcon />
              <div className="name paragraph-2xs center">{t("toolbox.border_fill")}</div>
            </div>
          </AutoGrid>
        </div>
      )}
      {hasBorder && (
        <>
          <div className="sliders">
            <Slider
              inputId="object_border_size"
              label={t("toolbox.size")}
              min={1}
              max={3}
              step={0.05}
              value={activeObject.border?.size}
              onChange={value => sign.updateObjectBorder({ size: value })}
              rangeMinIcon={FrameSizeSmallIcon}
              rangeMaxIcon={FrameSizeLargeIcon}
              hideInput={!env.TEST}
            />
          </div>
          <div className="sliders">
            <Slider
              inputId="object_border_thickness"
              label={t("toolbox.thickness")}
              min={0.1}
              max={1.9}
              step={0.05}
              value={activeObject.border?.thickness}
              onChange={value => sign.updateObjectBorder({ thickness: value })}
              rangeMinIcon={FrameThicknessSmallIcon}
              rangeMaxIcon={FrameThicknessLargeIcon}
              hideInput={!env.TEST}
            />
          </div>
        </>
      )}
      {disabledReasonShown && (
        <div className="disabled-reason paragraph-2xs bold mb-0">{t("toolbox.border_disabled_shape")}</div>
      )}
    </div>
  )
}

const ObjectBorderPicker: React.FC<ObjectBorderPickerProps> = props => {
  return window.innerWidth <= 1023 ? (
    <Scrollable>
      <ObjectBorderDetails {...props}></ObjectBorderDetails>
    </Scrollable>
  ) : (
    <ObjectBorderDetails {...props}></ObjectBorderDetails>
  )
}

export default connect(
  ({ currentSignData, subToolbox, activeObject, signState, signType }: State) => ({
    data: currentSignData,
    activeObject,
    subToolbox,
    signState,
    signType,
  }),
  {
    addSubToolbox: actions.addSubToolbox,
    setToolbox: actions.setToolbox,
  }
)(ObjectBorderPicker)
